import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import useApi from "../hooks/useApi.js";
import axios from "axios";
import { toast } from "react-toastify";
import Loading from "../components/Loading.js";
import AuthPromptDialog from "../components/AuthPromptDialog.js";
import {
  clearLocalSession,
  markSessionAlive,
  USER_STORAGE_KEY,
} from "../utils/session.js";
import { handleApiError } from "../utils/utils.js";

const AuthContext = createContext();

const AUTH_DEV_MODE =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_AUTH_DEV_MODE === "true";

const loadCachedUser = () => {
  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : null;
    // One-time migration for users cached under the old field name before the
    // avatar_url rename — without this, the first paint after deploy is avatar-less
    // until /token/auth resolves.
    if (parsed && !parsed.avatar_url && parsed.picture) {
      parsed.avatar_url = parsed.picture;
    }
    return parsed;
  } catch {
    return null;
  }
};

// Hook for child components to get the auth object and re-render when it changes
export const useAuth = () => useContext(AuthContext);

// Provider component that wraps your app and makes auth object available to any
// child component that calls useAuth().
export const AuthProvider = ({ children }) => {
  const { authenticateUser, logoutUser } = useApi();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogMessage, setAuthDialogMessage] = useState(null);

  // Cheap gate on "might be signed in". The cookie only hints at a session; the auth
  // check below is what decides.
  const isCookiePresent =
    !AUTH_DEV_MODE && document.cookie.includes("csrf_access_token");

  // Seed from localStorage so the app renders immediately with the last known
  // user rather than waiting for the auth check to complete.
  const [user, setUser] = useState(() => loadCachedUser());

  // True while waiting for the auth check when there's no cached user to show yet.
  // This covers the first load after login (localStorage was cleared on logout).
  // For returning users with a cached user, this starts false so the app renders immediately.
  const [isAuthLoading, setIsAuthLoading] = useState(
    AUTH_DEV_MODE || (isCookiePresent && loadCachedUser() === null)
  );

  const openAuthDialog = useCallback((message = null) => {
    setAuthDialogMessage(message);
    setAuthDialogOpen(true);
  }, []);

  const closeAuthDialog = useCallback(() => {
    setAuthDialogOpen(false);
    setAuthDialogMessage(null);
  }, []);

  // Deliberate, user-initiated logout. Only tear down the local session once the
  // backend confirms success: /logout never returns 401 (it clears cookies with
  // optional auth), so a failure here is a network/5xx error where the session
  // cookies may still be live. In that case we surface the error and keep the user
  // signed in rather than falsely claiming logout — a shared-device safety concern.
  const logout = useCallback(
    async (message, type = "success") => {
      try {
        if (AUTH_DEV_MODE) {
          delete axios.defaults.headers.common.Authorization;
        } else {
          await logoutUser();
        }
      } catch (error) {
        handleApiError(error);
        return;
      }
      setUser(null);
      clearLocalSession(message, type);
    },
    [logoutUser],
  );

  // Returns true once a redirect to the OAuth flow has been initiated (callers can
  // keep a loading state up through navigation), false if login could not start.
  const login = useCallback(async () => {
    if (AUTH_DEV_MODE) return false;
    try {
      await axios.get(`${process.env.REACT_APP_BACKEND_URL}/`, {
        timeout: 5000,
      });
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/oauth2/authorize`;
      return true;
    } catch (error) {
      toast.error("Login failed, please try again later");
      return false;
    }
  }, []);

  // Auth check on mount — establishes identity once per session.
  // - Success: update user state and persist to localStorage
  // - 401: token invalid/expired, log out
  // - Network error: keep cached user (already seeded from localStorage), show toast
  // - Other server errors: leave user as-is
  useEffect(() => {
    if (AUTH_DEV_MODE || !isCookiePresent) return;

    const checkAuth = async () => {
      try {
        const data = await authenticateUser();
        const { userDetails } = data;
        setUser(userDetails);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userDetails));
        // Session confirmed live: re-arm the dead-session redirect for this tab.
        markSessionAlive();
      } catch (error) {
        const status = error.response?.status;
        if (status === 401) {
          // Session is already dead — clear locally and redirect straight to login.
          // No /logout call: there's nothing to clear server-side, and we must not
          // route this through the success-gated logout() (which would surface a
          // spurious "logout failed" toast on an already-dead session).
          setUser(null);
          clearLocalSession(
            "You have been signed out, please log in again",
            "info",
          );
        } else if (!error.response) {
          // Toast is the only feedback — cached user remains so the app stays functional
          toast.error("You are currently offline", {
            autoClose: false,
            toastId: "offline",
          });
        }
      } finally {
        setIsAuthLoading(false);
      }
    };

    checkAuth();
    // authenticateUser is stable and clearLocalSession is a module import —
    // safe to omit from deps; this should run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dev mode: perform a mock login against the local dev-login endpoint
  useEffect(() => {
    if (!AUTH_DEV_MODE) return;

    const devLogin = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/dev-login`,
        );
        const { access_token, userDetails } = res.data;
        axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        setUser(userDetails);
      } catch (err) {
        console.error("Dev login failed", err);
        toast.error("Dev login failed");
      } finally {
        setIsAuthLoading(false);
      }
    };

    devLogin();
  }, []);

  // Called by profile/settings mutations to keep auth state in sync after edits
  const updateUser = useCallback((newUserDetails) => {
    setUser(newUserDetails);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(newUserDetails));
  }, []);

  if (isAuthLoading) {
    return <Loading />;
  }

  const value = {
    user,
    login,
    logout,
    updateUser,
    openAuthDialog,
    isAuthLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AuthPromptDialog
        open={authDialogOpen}
        onClose={closeAuthDialog}
        message={authDialogMessage}
      />
    </AuthContext.Provider>
  );
};